/**
 * @file CategoryTreeRow.tsx
 * @description Строка дерева категорий (phase19.31, образец (б) разбора B).
 *
 * С 1024 — строка 52: ручка, стрелка, название, вид товара, число товаров,
 * «На сайте» словами (да / по родителю / нет), «⋯» 32. До 1024 — строка 56:
 * ручка и стрелка по 48, название до двух строк (переносится между словами,
 * а не по одному слову), под ним «8 товаров · на сайте: да», справа «⋯» 48.
 * Число товаров — вместе с подкатегориями (`productsCountTotal`, контракт
 * §20); если в подкатегориях есть товары — подсказка «всего / в самой».
 * Вложенность — отступ 16px на уровень. Семи значков в строке больше нет:
 * всё, что меняет категорию, — в «⋯» (SC52). Системная — замок вместо ручки.
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronRight, GripVertical, Lock } from "lucide-react";
import type { CSSProperties } from "react";

import { categoryKindCaption } from "./category-row-meta";
import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import type { CatalogCategory } from "../../../lib/api-catalog";
import type { ProductKind } from "../../../lib/api-product-kinds";
import {
  categoryCountHint,
  categoryLabel,
  categoryProductCounts,
  effectivePublishOnSiteForCategory,
  type CategoryTreeNode,
} from "../../../lib/catalog-category-tree";
import { CATEGORY_PUBLISH_LABEL, categoryPublishMode } from "../../../lib/catalog-status";
import { pluralRu } from "../../../lib/plural";
import { cn } from "@/lib/utils";

/** Колонки строки с 1024 — общие со строкой-шапкой (`CategoryTreeList`). */
export const CATEGORY_GRID = "lg:grid lg:grid-cols-[minmax(0,1fr)_200px_88px_128px] lg:items-center lg:gap-4";

/** Кнопки-значки строки: 48 до 1024, 32 с 1024. */
const ICON_BTN = "flex size-12 shrink-0 items-center justify-center rounded-md text-on-surface-variant lg:size-8";

interface Props {
  node: CategoryTreeNode;
  open: boolean;
  onToggle: () => void;
  byId: Map<string, CatalogCategory>;
  kindsById: Map<string, ProductKind>;
  actions: RowAction[];
}

export function CategoryTreeRow({ node, open, onToggle, byId, kindsById, actions }: Props) {
  const locked = node.isSystemRoot;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id, disabled: locked });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  const hasChildren = node.children.length > 0;
  const label = categoryLabel(node);
  const mode = categoryPublishMode(node);
  const effective = effectivePublishOnSiteForCategory(node.id, byId);
  const publishHint = `Сейчас ${effective ? "показывается" : "не показывается"} на сайте · адрес /${node.slug}`;
  const kind = categoryKindCaption(node, kindsById);
  const counts = categoryProductCounts(node);
  const countHint = categoryCountHint(counts);
  const countText = counts ? `${counts.total} ${pluralRu(counts.total, ["товар", "товара", "товаров"])}` : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex min-h-14 items-center border-b border-[var(--border)] bg-surface-lowest last:border-b-0 lg:min-h-13",
        isDragging && "relative z-10 shadow-md",
      )}
    >
      <div className="flex shrink-0 items-center" style={{ paddingLeft: `${Math.min(node.depth, 3) * 16}px` }}>
        {locked ? (
          <span className={ICON_BTN} title="Системная категория — её нельзя менять">
            <Lock className="size-4" aria-hidden />
            <span className="sr-only">Системная категория</span>
          </span>
        ) : (
          <button type="button" {...attributes} {...listeners} aria-label={`Перетащить «${label}»`} className={cn(ICON_BTN, "cursor-grab hover:bg-surface-low active:cursor-grabbing")}>
            <GripVertical className="size-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          disabled={!hasChildren}
          aria-expanded={hasChildren ? open : undefined}
          aria-label={open ? `Свернуть «${label}»` : `Раскрыть «${label}»`}
          className={cn(ICON_BTN, "hover:bg-surface-low", !hasChildren && "invisible")}
        >
          <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} />
        </button>
      </div>

      <div className={cn("min-w-0 flex-1 py-2 pl-1", CATEGORY_GRID)}>
        <div className="min-w-0">
          <p title={label} className={cn("type-body line-clamp-2 font-medium [overflow-wrap:normal] lg:line-clamp-1", locked ? "text-on-surface-variant" : "text-on-surface")}>
            {label}
          </p>
          <p className="type-caption text-on-surface-variant lg:hidden" title={[countHint, publishHint].filter(Boolean).join(" · ")}>
            {[countText, `на сайте: ${CATEGORY_PUBLISH_LABEL[mode]}`, kind?.text].filter(Boolean).join(" · ")}
          </p>
        </div>
        <span className={cn("type-caption hidden truncate lg:block", kind?.attention ? "text-[var(--status-attention-fg,var(--warning))]" : "text-on-surface-variant")}>
          {kind?.text ?? "—"}
        </span>
        <span className="type-body hidden text-right tabular-nums lg:block" title={countHint ?? undefined}>
          {counts?.total ?? "—"}
          {countHint ? <span className="sr-only"> ({countHint})</span> : null}
        </span>
        <span className="type-body hidden lg:block" title={publishHint}>
          {CATEGORY_PUBLISH_LABEL[mode]}
        </span>
      </div>

      <div className="flex w-14 shrink-0 justify-end pr-2">
        <RowActionsMenu actions={actions} size="compact" title={`Категория «${label}»`} ariaLabel={`Действия: ${label}`} />
      </div>
    </div>
  );
}
